import { faPlus, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from '@tanstack/react-router';
import { getGetGroupByIdQueryKey } from '@uturn/api/v1';
import {
	type GetCurrentValueOfFieldInGroup200,
	type MultiUpdateRequestDto,
	type UpdateFieldsInGroupParams,
	type UpdateFieldTypeDto,
	useGetCurrentValueOfFieldInGroup,
	useGetUpdateFieldsByStatus,
	useUpdateFieldsInGroup,
} from '@uturn/api/v2';
import { queryClient } from '@uturn/portal/query';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import type { StatusTab } from '@uturn/portal/types/shipments';
import { getApiErrorMessage } from '@uturn/portal/utils';
import { unsanitize } from '@uturn/portal/utils/unsanitize';
import {
	Button,
	Callout,
	CalloutIcon,
	CalloutTitle,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	Icon,
	ScrollArea,
	sonner,
} from '@uturn/ui-kit';
import type { AxiosResponse } from 'axios';
import posthog from 'posthog-js';
import { useContext, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';
import { ShipmentGroupDetailContext } from '../../providers/shipment-group-detail';
import { computeNeededDateKeys, makeEnabledFor } from './date-baselines';
import {
	DATE_FIELD_KEYS,
	type DateFieldKey,
	FIELD_KEYS,
	isDateFieldKey,
	isPublishStateFieldKey,
	type PublishStateFieldKey,
} from './field-keys';
import { FieldRow } from './field-row';
import { useMultiUpdateDatesValidationAdapter } from './hooks/useMultiUpdateDatesValidationAdapter';
import { MultiUpdateConfirmationDialog } from './multi-update-confirmation-dialog';
import type { UpdateFilters } from './multi-update-dialog';
import { isImpactKey, isQuoteImpactKey } from './multi-update-impact';
import { createFormSchema } from './multi-update-schema';
import type { FieldOption, Row } from './multi-update-types';
import { MultiUpdateUnsavedChangesDialog } from './multi-update-unsaved-changes-dialog';
import {
	mapRowsToMultiUpdateRequestPayload,
	PUBLISH_STATE_BASELINE,
	removeFilterPrefix,
	toFieldOptions,
} from './multi-update-utils';

const PUBLISH_STATE_FIELD_BY_TAB: Partial<
	Record<StatusTab, PublishStateFieldKey>
> = {
	drafted: FIELD_KEYS.PUBLISH,
	published: FIELD_KEYS.UNPUBLISH,
};

export const MultiUpdateForm = ({
	setOpenModal,
	isDirtyRef,
	exportFilters,
	preselectPublish = false,
}: {
	setOpenModal: (open: boolean) => void;
	isDirtyRef: React.MutableRefObject<boolean>;
	exportFilters: UpdateFilters;
	preselectPublish?: boolean;
}) => {
	const { t } = useTranslation();
	const { groupId } = useParams({
		from: '/_protected/shipments/groups/$groupId',
	});
	const { groupName } = useContext(ShipmentGroupDetailContext);

	const { totalShipments, activeStatusTab } = useShipmentsSearchStore();

	const groupStatus =
		activeStatusTab === 'assigned' ? 'assigned' : 'unassigned';
	const isDraftedTab = activeStatusTab === 'drafted';

	const publishStateFieldKey = PUBLISH_STATE_FIELD_BY_TAB[activeStatusTab];

	const filters = useMemo(
		() => removeFilterPrefix(exportFilters),
		[exportFilters],
	);

	const { data: apiFieldOptions = [], isLoading: areFieldOptionsLoading } =
		useGetUpdateFieldsByStatus<UpdateFieldTypeDto[]>(
			{
				status: groupStatus,
			},
			{
				query: {
					select: (resp) => resp.data,
				},
			},
		);

	// Normalize to the UI type
	const fieldOptions: FieldOption[] = useMemo(() => {
		if (areFieldOptionsLoading) {
			return [];
		}

		const options = toFieldOptions(apiFieldOptions).filter(
			(option) => !isPublishStateFieldKey(option.name),
		);

		if (!publishStateFieldKey) {
			return options;
		}

		return [
			...options,
			{ name: publishStateFieldKey, inputType: 'boolean' as const },
		];
	}, [apiFieldOptions, publishStateFieldKey, areFieldOptionsLoading]);

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

	const formRef = useRef<HTMLFormElement>(null);
	const schema = useMemo(() => createFormSchema(fieldOptions), [fieldOptions]);
	type Schema = z.infer<typeof schema>;

	const EMPTY_ROW = { key: '', currentValues: [], value: '' };

	// Pre-seed the publish/unpublish row when opened from the dedicated action.
	const defaultRows =
		preselectPublish && publishStateFieldKey
			? [
					{
						key: publishStateFieldKey,
						currentValues: [PUBLISH_STATE_BASELINE[publishStateFieldKey]],
						value: !PUBLISH_STATE_BASELINE[publishStateFieldKey],
					},
				]
			: [EMPTY_ROW];

	const form = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			rows: defaultRows,
		},
	});

	const {
		control,
		handleSubmit,
		formState: { errors, isDirty, isValid },
	} = form;

	isDirtyRef.current = isDirty;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'rows',
	});

	const rows = (useWatch({ control, name: 'rows' }) as Row[]) || [];

	const rowMap = useMemo(() => {
		return (rows ?? []).reduce<Map<string, Row>>((m, r) => {
			if (r && typeof r.key === 'string' && r.key.length > 0) {
				m.set(r.key, r);
			}
			return m;
		}, new Map());
	}, [rows]);

	const selectedKeys = useMemo(
		() =>
			(rows as Array<{ key?: string }>)
				.map((r) => r?.key)
				.filter((k): k is string => Boolean(k)),
		[rows],
	);

	const publishStateValue = publishStateFieldKey
		? rowMap.get(publishStateFieldKey)?.value
		: undefined;
	const publishing =
		typeof publishStateValue === 'boolean' ? publishStateValue : !isDraftedTab;

	const isPublishAction = isDraftedTab && publishing;

	const selectedDateKeys = useMemo(() => {
		const onlyDates = (rows ?? [])
			.map((r) => r?.key)
			.filter(isDateFieldKey) as DateFieldKey[];
		return new Set<DateFieldKey>(onlyDates);
	}, [rows]);

	const neededDateKeys = useMemo(
		() =>
			computeNeededDateKeys({
				rowMap,
				selectedDateKeys,
				skipPublishUntilValidation: false,
				isPublishAction,
			}),
		[rowMap, selectedDateKeys, isPublishAction],
	);

	const enabledFor = makeEnabledFor(neededDateKeys, groupId);

	const commonQuerySelect = useMemo(
		() => (res: AxiosResponse<GetCurrentValueOfFieldInGroup200, unknown>) =>
			res?.data?.value ?? res?.data?.values ?? res?.data ?? res ?? [],
		[],
	);

	const pickupDateFromQuery = useGetCurrentValueOfFieldInGroup(
		Number(groupId),
		FIELD_KEYS.PICKUP_DATE_FROM,
		{ status: groupStatus, ...filters },
		{
			query: {
				select: commonQuerySelect,
				enabled: enabledFor(FIELD_KEYS.PICKUP_DATE_FROM),
			},
		},
	);
	const pickupDateUntilQuery = useGetCurrentValueOfFieldInGroup(
		Number(groupId),
		FIELD_KEYS.PICKUP_DATE_UNTIL,
		{ status: groupStatus, ...filters },
		{
			query: {
				select: commonQuerySelect,
				enabled: enabledFor(FIELD_KEYS.PICKUP_DATE_UNTIL),
			},
		},
	);
	const dropDateFromQuery = useGetCurrentValueOfFieldInGroup(
		Number(groupId),
		FIELD_KEYS.DROP_DATE_FROM,
		{ status: groupStatus, ...filters },
		{
			query: {
				select: commonQuerySelect,
				enabled: enabledFor(FIELD_KEYS.DROP_DATE_FROM),
			},
		},
	);
	const dropDateUntilQuery = useGetCurrentValueOfFieldInGroup(
		Number(groupId),
		FIELD_KEYS.DROP_DATE_UNTIL,
		{ status: groupStatus, ...filters },
		{
			query: {
				select: commonQuerySelect,
				enabled: enabledFor(FIELD_KEYS.DROP_DATE_UNTIL),
			},
		},
	);
	const publishUntilQuery = useGetCurrentValueOfFieldInGroup(
		Number(groupId),
		FIELD_KEYS.PUBLISH_UNTIL,
		{ status: groupStatus, ...filters },
		{
			query: {
				select: commonQuerySelect,
				enabled: enabledFor(FIELD_KEYS.PUBLISH_UNTIL),
			},
		},
	);

	const dateBaselines = useMemo(
		() => ({
			[FIELD_KEYS.PICKUP_DATE_FROM]: pickupDateFromQuery.data ?? [],
			[FIELD_KEYS.PICKUP_DATE_UNTIL]: pickupDateUntilQuery.data ?? [],
			[FIELD_KEYS.DROP_DATE_FROM]: dropDateFromQuery.data ?? [],
			[FIELD_KEYS.DROP_DATE_UNTIL]: dropDateUntilQuery.data ?? [],
			[FIELD_KEYS.PUBLISH_UNTIL]: publishUntilQuery.data ?? [],
		}),
		[
			pickupDateFromQuery.data,
			pickupDateUntilQuery.data,
			dropDateFromQuery.data,
			dropDateUntilQuery.data,
			publishUntilQuery.data,
		],
	);

	const areBaselinesLoading =
		pickupDateFromQuery.isLoading ||
		pickupDateFromQuery.isFetching ||
		pickupDateUntilQuery.isLoading ||
		pickupDateUntilQuery.isFetching ||
		dropDateFromQuery.isLoading ||
		dropDateFromQuery.isFetching ||
		dropDateUntilQuery.isLoading ||
		dropDateUntilQuery.isFetching ||
		publishUntilQuery.isLoading ||
		publishUntilQuery.isFetching;

	const isImpactField = useMemo(
		() => selectedKeys.some((k) => isImpactKey(k)),
		[selectedKeys],
	);
	const isQuoteImpactField = useMemo(
		() => selectedKeys.some((k) => isQuoteImpactKey(k)),
		[selectedKeys],
	);
	const isUnpublish = selectedKeys.includes(FIELD_KEYS.UNPUBLISH);

	const handleAddField = async () => {
		append({ key: '', currentValues: [], value: '' });
	};

	const { hasDateErrors } = useMultiUpdateDatesValidationAdapter({
		form,
		baselines: dateBaselines,
		publishing,
		skipPublishUntilValidation: false,
	});

	// Date fields the publish needs that aren't on screen, so nothing blocks Update invisibly.
	const missingPublishDateKeys = useMemo(() => {
		const offeredKeys = new Set(fieldOptions.map((option) => option.name));
		return DATE_FIELD_KEYS.filter(
			(key) => offeredKeys.has(key) && !rowMap.has(key),
		);
	}, [fieldOptions, rowMap]);

	const showStaleDatesCallout =
		isPublishAction && hasDateErrors && missingPublishDateKeys.length > 0;

	const handleAddMissingDateFields = () => {
		for (const key of missingPublishDateKeys) {
			append({ key, currentValues: dateBaselines[key] ?? [], value: '' });
		}
	};

	const { mutateAsync, isPending } = useUpdateFieldsInGroup();

	const onSubmit = async (data: Schema) => {
		const payload: MultiUpdateRequestDto = mapRowsToMultiUpdateRequestPayload(
			data.rows,
		);

		const filters = removeFilterPrefix(exportFilters);
		const params: UpdateFieldsInGroupParams = {
			status: groupStatus,
			...filters,
		};

		await mutateAsync(
			{
				groupId: Number(groupId),
				data: payload,
				params,
			},
			{
				onSuccess: async () => {
					sonner.success(
						t(
							'pages.multi-update-shipments.dialog.multi-update-request.success.description',
							'Your shipment(s) have been successfully updated',
						),
					);
					const groupQueryKey = getGetGroupByIdQueryKey(Number(groupId));
					await queryClient.invalidateQueries({
						queryKey: groupQueryKey,
					});
					posthog.capture(`bulk_update_${groupStatus}`, {
						name: 'button_bulk_update',
						group_id: Number(groupId),
						group_status: groupStatus,
						shipments_count: totalShipments ?? 0,
						edited_field_count: data.rows.length,
						is_impact_field: isImpactField,
						is_quote_impact_field: isQuoteImpactField,
					});
					form.reset({ rows: [EMPTY_ROW] });

					//TODO Refresh the Meilisearch table

					setOpenModal(false);
				},
				onError: (error) => {
					const message = getApiErrorMessage(error);
					if (message) {
						sonner.error(message);
						posthog.capture('bulk_update_error_custom', message);
						return;
					}
					sonner.error(
						t(
							'pages.multi-update-shipments.dialog.multi-update-request.error.description',
							'Something went wrong while updating your shipment(s)',
						),
					);
					posthog.capture('bulk_update_error_default', {
						error_message:
							'Something went wrong while updating your shipment(s)',
					});
				},
				onSettled: () => {
					setIsConfirmOpen(false); // always close confirm, success or error
				},
			},
		);
	};

	return (
		<div className="px-2 flex max-h-[55vh] min-h-[55vh] flex-col">
			<DialogHeader>
				<DialogTitle className="mb-4 text-center">
					{t(
						'pages.multi-update-shipments.dialog.multi-update-request.heading',
						'Multi update {{count}} shipments from "{{group}}" group',
						{
							count: totalShipments,
							group: groupName,
						},
					)}
				</DialogTitle>
			</DialogHeader>
			<div className="flex-1 min-h-0">
				<ScrollArea className="h-full">
					<Form {...form}>
						<form
							onSubmit={handleSubmit(onSubmit)}
							ref={formRef}
							className="px-2"
						>
							<div className="text-muted-foreground mb-2 grid grid-cols-[1fr_1fr_1fr_auto] text-sm font-light">
								<span>
									{t('pages.multi-update-shipments.field-label', 'Field')}
								</span>
								<span>
									{t(
										'pages.multi-update-shipments.current-value-label',
										'Current value',
									)}
								</span>
								<span>
									{t(
										'pages.multi-update-shipments.new-value-label',
										'New value',
									)}
								</span>
							</div>

							{fields.map((field, index) => (
								<FieldRow
									key={field.id}
									index={index}
									fieldOptions={fieldOptions}
									control={control}
									onRemove={remove}
									canRemove={fields.length > 1}
									groupStatus={groupStatus}
									groupId={Number(groupId)}
									filters={filters}
									fieldOptionsLoading={areFieldOptionsLoading}
								/>
							))}

							{showStaleDatesCallout && (
								<Callout variant="warning" className="mt-2 items-start">
									<CalloutIcon>
										<Icon icon={faWarning} />
									</CalloutIcon>
									<div className="flex flex-col items-start gap-2">
										<CalloutTitle>
											{t(
												'pages.multi-update-shipments.stale-dates.description',
												'These shipments have dates in the past. Publishing needs all of these set to future dates:',
											)}
										</CalloutTitle>
										<ul className="list-inside list-disc text-sm">
											{missingPublishDateKeys.map((key) => (
												<li key={key}>
													{t(
														`pages.multi-update-shipments.select-options.${key}`,
														unsanitize(key),
													)}
												</li>
											))}
										</ul>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={handleAddMissingDateFields}
											id="multi-update-add-date-fields-button"
										>
											<Icon icon={faPlus} className="mr-2" />
											{t(
												'pages.multi-update-shipments.stale-dates.add-fields',
												'Add these date fields',
											)}
										</Button>
									</div>
								</Callout>
							)}

							{errors.rows?.message && (
								<p className="text-sm text-red-500">{errors.rows.message}</p>
							)}
						</form>
					</Form>
				</ScrollArea>
			</div>
			<DialogFooter className="mt-6 flex w-full justify-center">
				<div className="flex w-full flex-col items-center gap-3">
					<Button
						type="button"
						variant="ghost"
						onClick={handleAddField}
						className="text-foreground"
						id="multi-update-add-field-button"
					>
						<Icon icon={faPlus} className="mr-2" />
						{t('pages.multi-update-shipments.add-field-label', 'Add field')}
					</Button>

					<Button
						type="button"
						className="min-w-[200px]"
						disabled={!isValid || hasDateErrors || areBaselinesLoading}
						onClick={() => setIsConfirmOpen(true)}
						id="multi-update-update-button"
					>
						{t(
							'pages.multi-update-shipments.dialog.multi-update-request.confirm',
							'Update',
						)}
					</Button>
				</div>
			</DialogFooter>
			<MultiUpdateConfirmationDialog
				open={isConfirmOpen}
				onCancel={() => setIsConfirmOpen(false)}
				onConfirm={handleSubmit(async (data) => {
					await onSubmit(data);
					setIsConfirmOpen(false);
				})}
				isSubmitting={isPending}
				groupStatus={groupStatus}
				isImpactField={isImpactField}
				isQuoteImpactField={isQuoteImpactField}
				isUnpublish={isUnpublish}
				totalShipments={totalShipments ?? 0}
				groupId={Number(groupId)}
				filters={filters}
			/>
			<MultiUpdateUnsavedChangesDialog
				open={showUnsavedDialog}
				onCancel={() => setShowUnsavedDialog(false)}
				onConfirm={() => {
					setShowUnsavedDialog(false);
					setOpenModal(false);
				}}
			/>
		</div>
	);
};
